/**
 * AppLayout - Layout principale dell'applicazione
 * Il Diario di Bordo - Frontend
 * 
 * FASE 5: Aggiunto supporto per navigazione Responsabile
 * FIX: Aggiunta prop 'end' ai NavLink Dashboard per match esatto URL
 */

import { useState, useEffect } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { useIdleLogout } from '../../hooks/useIdleLogout'
import { OnlineIndicator } from '../../components/ui/OnlineIndicator'
import GlobalSearch, { useGlobalSearch } from '../../components/ui/GlobalSearch'
import NotificationBell from '../NotificationBell'
import api from '../../services/api'
import EmailVerificationBanner from '../ui/EmailVerificationBanner'
import { useFeature } from '../../hooks/useFeature'
import {
  LayoutDashboard,
  Baby,
  FileText,
  Plus,
  Users,
  Settings,
  BarChart3,
  LogOut,
  Menu,
  X,
  Moon,
  Sun,
  User,
  CreditCard,
  ChevronDown,
  Info,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Search,
  Wrench,
  Trash2,
  Building2,
  FileCheck,
  Receipt,
  Zap,
  UsersRound,
  Bell
} from 'lucide-react'

// Componente Banner Annunci
const AnnouncementBanner = ({ announcement, onDismiss, onView, onClick }) => {
  const typeConfig = {
    info: { bg: 'bg-blue-50 dark:bg-blue-900/30', border: 'border-blue-200 dark:border-blue-800', text: 'text-blue-800 dark:text-blue-200', icon: Info },
    success: { bg: 'bg-green-50 dark:bg-green-900/30', border: 'border-green-200 dark:border-green-800', text: 'text-green-800 dark:text-green-200', icon: CheckCircle },
    warning: { bg: 'bg-yellow-50 dark:bg-yellow-900/30', border: 'border-yellow-200 dark:border-yellow-800', text: 'text-yellow-800 dark:text-yellow-200', icon: AlertTriangle },
    critical: { bg: 'bg-red-50 dark:bg-red-900/30', border: 'border-red-200 dark:border-red-800', text: 'text-red-800 dark:text-red-200', icon: AlertTriangle }
  }

  const config = typeConfig[announcement.type] || typeConfig.info
  const Icon = config.icon

  useEffect(() => {
    // Incrementa views quando visibile
    if (onView) onView(announcement._id)
  }, [])

  const handleLinkClick = () => {
    if (onClick) onClick(announcement._id)
    if (announcement.linkUrl) {
      window.open(announcement.linkUrl, '_blank')
    }
  }

  return (
    <div className={`${config.bg} ${config.border} border-b px-4 py-3`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <Icon className={`h-5 w-5 ${config.text} flex-shrink-0`} />
          <div className="flex-1">
            <p className={`text-sm font-medium ${config.text}`}>
              {announcement.title}
            </p>
            {announcement.message && (
              <p className={`text-xs ${config.text} opacity-80 mt-0.5`}>
                {announcement.message}
              </p>
            )}
          </div>
          {announcement.linkUrl && (
            <button
              onClick={handleLinkClick}
              className={`text-sm font-medium ${config.text} hover:underline flex items-center gap-1`}
            >
              {announcement.linkText || 'Scopri di più'}
              <ExternalLink className="h-3 w-3" />
            </button>
          )}
        </div>
        {announcement.dismissible && (
          <button
            onClick={() => onDismiss(announcement._id)}
            className={`p-1 hover:bg-black/5 dark:hover:bg-white/5 rounded ${config.text}`}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [announcements, setAnnouncements] = useState([])
  const [maintenanceMode, setMaintenanceMode] = useState(() => {
    return localStorage.getItem('maintenanceMode') === 'true'
  })
  const [maintenanceMessage, setMaintenanceMessage] = useState(() => {
    return localStorage.getItem('maintenanceMessage') || 'Il sistema è in manutenzione. Torneremo online a breve.'
  })
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('dismissedAnnouncements') || '{}')
    } catch {
      return {}
    }
  })

  // FASE 5: Aggiunto isResponsabile
  const { user, tenant, organization, logout, isCoordinatore, isResponsabile } = useAuth()
  const { darkMode, toggleDarkMode } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  
  // Global Search
  const { isOpen: searchOpen, open: openSearch, close: closeSearch } = useGlobalSearch()

  // Feature toggles
  const quickCheckin = useFeature('quickCheckin')
  const attivitaGruppo = useFeature('attivitaGruppo')
  // Smart alerts ora confluiscono nel Centro Notifiche unificato (NotificationBell)
  // tramite dispatch in services/alertEngine.js. Niente più polling legacy qui.

  // Listener per evento manutenzione
  useEffect(() => {
    const handleMaintenance = (event) => {
      setMaintenanceMode(true)
      setMaintenanceMessage(event.detail?.message || 'Il sistema è in manutenzione. Torneremo online a breve.')
    }
    
    window.addEventListener('maintenance-mode', handleMaintenance)
    return () => window.removeEventListener('maintenance-mode', handleMaintenance)
  }, [])

  // Carica annunci
  useEffect(() => {
    loadAnnouncements()
  }, [])

  const loadAnnouncements = async () => {
    try {
      const res = await api.get('/public/announcements')
      const active = res.data.data?.announcements || []
      // Filtra annunci già dismissati
      const filtered = active.filter(a => !dismissedAnnouncements[a._id])
      setAnnouncements(filtered)
    } catch (e) {}
  }

  const handleDismissAnnouncement = (id) => {
    const updated = { ...dismissedAnnouncements, [id]: true }
    setDismissedAnnouncements(updated)
    localStorage.setItem('dismissedAnnouncements', JSON.stringify(updated))
    setAnnouncements(prev => prev.filter(a => a._id !== id))
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // Logout automatico dopo 30 minuti di inattività
  useIdleLogout(handleLogout, 30 * 60 * 1000)

  // Navigazione principale (Educatore/Coordinatore)
  // FIX: Aggiunto end: true per Dashboard per match esatto URL
  const mainNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, end: true },
    { name: 'Minori', href: '/minori', icon: Baby },
    { name: 'Registrazioni', href: '/entries', icon: FileText },
    ...(quickCheckin.visible ? [{ name: 'Check-in', href: '/checkin', icon: Zap }] : []),
    ...(attivitaGruppo.visible ? [{ name: 'Attività Gruppo', href: '/group-activity', icon: UsersRound }] : []),
    { name: 'Report', href: '/reports', icon: BarChart3 },
    // Centro Comunicazioni unificato (notifiche + messaggi + promemoria + impostazioni)
    { name: 'Comunicazioni', href: '/notifiche', icon: Bell },
    { name: 'Cestino', href: '/trash', icon: Trash2 },
  ]

  // Navigazione solo coordinatore
  const coordinatoreNavigation = [
    { name: 'Utenti', href: '/users', icon: Users },
    { name: 'Impostazioni', href: '/settings', icon: Settings },
    { name: 'Abbonamento', href: '/subscription', icon: CreditCard },
  ]

  // FASE 5: Navigazione Responsabile
  // FIX: Aggiunto end: true per Dashboard per match esatto URL
  const responsabileNavigation = [
    { name: 'Dashboard', href: '/organization', icon: LayoutDashboard, end: true },
    { name: 'Centri', href: '/organization/centers', icon: Building2 },
    // Centro Comunicazioni unificato (notifiche + messaggi + promemoria + impostazioni)
    { name: 'Comunicazioni', href: '/organization/notifiche', icon: Bell },
    { name: 'Documenti', href: '/organization/contracts', icon: FileCheck },
    { name: 'Fatturazione', href: '/organization/billing', icon: Receipt },
    { name: 'Impostazioni', href: '/organization/settings', icon: Settings },
  ]

  // Bottom navigation mobile (5 icone)
  // FIX: Aggiunto end: true per Home per match esatto URL
  const bottomNavigation = isResponsabile() ? [
    { name: 'Home', href: '/organization', icon: LayoutDashboard, end: true },
    { name: 'Centri', href: '/organization/centers', icon: Building2 },
    { name: 'Nuovo', href: '/organization/centers/new', icon: Plus, center: true },
    { name: 'Docs', href: '/organization/contracts', icon: FileCheck },
    { name: 'Menu', action: 'menu', icon: Menu },
  ] : [
    { name: 'Home', href: '/dashboard', icon: LayoutDashboard, end: true },
    { name: 'Minori', href: '/minori', icon: Baby },
    { name: 'Nuovo', href: '/entries/new', icon: Plus, center: true },
    { name: 'Entry', href: '/entries', icon: FileText },
    { name: 'Menu', action: 'menu', icon: Menu },
  ]

  // FIX: Modificata funzione isActive per supportare parametro end
  const isActive = (href, end = false) => {
    if (end) {
      return location.pathname === href
    }
    return location.pathname === href || location.pathname.startsWith(href + '/')
  }

  // Annunci da mostrare in alto
  const topAnnouncements = announcements.filter(a => a.position !== 'bottom')

  // Overlay manutenzione
  if (maintenanceMode) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <Wrench className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Sistema in Manutenzione
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {maintenanceMessage}
          </p>
          <button
            onClick={() => {
              localStorage.removeItem('maintenanceMode')
              window.location.reload()
            }}
            className="btn-primary"
          >
            Riprova
          </button>
        </div>
      </div>
    )
  }

  // Determina quale navigazione mostrare
  const currentNavigation = isResponsabile() 
    ? responsabileNavigation 
    : mainNavigation

  const showCoordinatoreSection = isCoordinatore() && !isResponsabile()

  // Nome da mostrare (organization o tenant)
  const displayName = isResponsabile() 
    ? organization?.name || 'Organization' 
    : tenant?.name || 'Il Diario di Bordo'

  // Ruolo da mostrare
  const displayRole = isResponsabile() 
    ? 'Responsabile' 
    : isCoordinatore() 
      ? 'Coordinatore' 
      : 'Educatore'

  // FIX: Link al profilo basato sul ruolo
  const profileLink = isResponsabile() ? '/organization/profile' : '/profile'

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Online/Offline indicator */}
      <OnlineIndicator />
      
      {/* Global Search */}
      <GlobalSearch />

      {/* Annunci */}
      {topAnnouncements.map(ann => (
        <AnnouncementBanner
          key={ann._id}
          announcement={ann}
          onDismiss={handleDismissAnnouncement}
        />
      ))}

      {/* Email verification banner (solo coordinatori) */}
      <EmailVerificationBanner />

      {/* === SIDEBAR DESKTOP === */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
        {/* Logo */}
        <div className="flex items-center h-16 px-6 border-b border-gray-200 dark:border-gray-700">
          <img 
            src={tenant?.branding?.logoUrl || '/logo.png'} 
            alt={displayName} 
            className="h-16 w-16 object-contain rounded"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'inline'; }}
          />
          <span className="text-2xl" style={{ display: 'none' }}>📘</span>
          <span className="ml-3 font-bold text-lg text-gray-900 dark:text-white truncate">
            {displayName}
          </span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {/* FIX: Aggiunto end={item.end} per match esatto URL */}
          {currentNavigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`
              }
            >
              <item.icon className="h-5 w-5 mr-3" />
              {item.name}
            </NavLink>
          ))}

          {/* Sezione coordinatore (solo se non responsabile) */}
          {showCoordinatoreSection && (
            <>
              <div className="pt-4 pb-2">
                <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Gestione
                </p>
              </div>
              {coordinatoreNavigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    `flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`
                  }
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.name}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        {/* User section */}
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <NavLink
              to={profileLink}
              className="flex items-center flex-1 min-w-0 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg p-2 -m-2"
            >
              <div className="w-9 h-9 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                <User className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="ml-3 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {user?.nome} {user?.cognome}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {displayRole}
                </p>
              </div>
            </NavLink>

            <div className="flex items-center space-x-1">
              {/* Centro Notifiche — dropdown si apre a destra del bell (la sidebar è stretta) */}
              <NotificationBell dropdownPlacement="right-bottom" />
              <button
                onClick={toggleDarkMode}
                className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                title={darkMode ? 'Tema chiaro' : 'Tema scuro'}
              >
                {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                title="Esci"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* === MAIN CONTENT === */}
      <div className="lg:pl-64">
        {/* Header mobile */}
        <header className="lg:hidden sticky top-0 z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between h-14 px-4">
            <div className="flex items-center min-w-0">
              <img 
                src={tenant?.branding?.logoUrl || '/logo.png'} 
                alt={displayName} 
                className="h-11 w-11 object-contain rounded flex-shrink-0"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'inline'; }}
              />
              <span className="text-xl" style={{ display: 'none' }}>📘</span>
              <span className="ml-2 font-semibold text-gray-900 dark:text-white truncate">
                {displayName}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              {/* Search button mobile */}
              <button
                onClick={openSearch}
                className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <Search className="h-5 w-5" />
              </button>
              {/* Centro Notifiche unificato (aggrega smart alert + tutto il resto) */}
              <NotificationBell />
              <button
                onClick={toggleDarkMode}
                className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="pb-20 lg:pb-0">
          <Outlet />
        </main>
      </div>

      {/* === BOTTOM NAVIGATION MOBILE === */}
      <nav className="lg:hidden bottom-nav">
        <div className="flex items-center justify-around h-16">
          {bottomNavigation.map((item) => {
            if (item.action === 'menu') {
              return (
                <button
                  key={item.name}
                  onClick={() => setMobileMenuOpen(true)}
                  className="bottom-nav-item"
                >
                  <item.icon className="h-6 w-6" />
                  <span className="text-xs mt-1">{item.name}</span>
                </button>
              )
            }

            if (item.center) {
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className="bottom-nav-item-center"
                >
                  <item.icon className="h-7 w-7" />
                </NavLink>
              )
            }

            {/* FIX: Aggiunto item.end per match esatto URL */}
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={`bottom-nav-item ${isActive(item.href, item.end) ? 'active' : ''}`}
              >
                <item.icon className="h-6 w-6" />
                <span className="text-xs mt-1">{item.name}</span>
              </NavLink>
            )
          })}
        </div>
      </nav>

      {/* === MOBILE MENU OVERLAY === */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-2xl animate-slide-up">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Menu</h3>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
              {/* User info */}
              <NavLink
                to={profileLink}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                  <User className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="ml-3">
                  <p className="font-medium text-gray-900 dark:text-white">
                    {user?.nome} {user?.cognome}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {displayRole}
                  </p>
                </div>
              </NavLink>

              {/* Centro Comunicazioni unificato (notifiche + messaggi + promemoria) */}
              <NavLink
                to={isResponsabile() ? '/organization/notifiche' : '/notifiche'}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center p-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <Bell className="h-5 w-5 mr-3 text-gray-500" />
                <span className="text-gray-900 dark:text-white">Comunicazioni</span>
              </NavLink>

              {/* Report */}
              {!isResponsabile() && (
                <NavLink
                  to="/reports"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center p-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  <BarChart3 className="h-5 w-5 mr-3 text-gray-500" />
                  <span className="text-gray-900 dark:text-white">Report</span>
                </NavLink>
              )}

              {/* Cestino (non per responsabile) */}
              {!isResponsabile() && (
                <NavLink
                  to="/trash"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center p-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  <Trash2 className="h-5 w-5 mr-3 text-gray-500" />
                  <span className="text-gray-900 dark:text-white">Cestino</span>
                </NavLink>
              )}

              {/* Coordinatore only (non per responsabile) */}
              {showCoordinatoreSection && (
                <>
                  <div className="pt-2 pb-1">
                    <p className="px-3 text-xs font-semibold text-gray-400 uppercase">
                      Gestione
                    </p>
                  </div>
                  {coordinatoreNavigation.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center p-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    >
                      <item.icon className="h-5 w-5 mr-3 text-gray-500" />
                      <span className="text-gray-900 dark:text-white">{item.name}</span>
                    </NavLink>
                  ))}
                </>
              )}

              {/* Responsabile extra menu items */}
              {isResponsabile() && (
                <>
                  <div className="pt-2 pb-1">
                    <p className="px-3 text-xs font-semibold text-gray-400 uppercase">
                      Organizzazione
                    </p>
                  </div>
                  <NavLink
                    to="/organization/contracts"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center p-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    <FileCheck className="h-5 w-5 mr-3 text-gray-500" />
                    <span className="text-gray-900 dark:text-white">Documenti Legali</span>
                  </NavLink>
                  <NavLink
                    to="/organization/billing"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center p-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    <Receipt className="h-5 w-5 mr-3 text-gray-500" />
                    <span className="text-gray-900 dark:text-white">Fatturazione</span>
                  </NavLink>
                  <NavLink
                    to="/organization/settings"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center p-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    <Settings className="h-5 w-5 mr-3 text-gray-500" />
                    <span className="text-gray-900 dark:text-white">Impostazioni</span>
                  </NavLink>
                </>
              )}

              {/* Logout */}
              <button
                onClick={() => {
                  setMobileMenuOpen(false)
                  handleLogout()
                }}
                className="flex items-center w-full p-3 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
              >
                <LogOut className="h-5 w-5 mr-3" />
                <span>Esci</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
